import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Save, Trash2, X, Check, Ban, Sparkles, Search } from "lucide-react";

const DRAFT_THEMES = [
  "Nutritional Medicine", "Herbal Medicine", "Plant Medicine", "Environmental Medicine", "Agroecology",
  "Indigenous Farming", "Precision Medicine", "Indigenous Science",
];

const IMG_STYLES = [
  { id: "botanical", label: "Botanical" },
  { id: "photographic", label: "Photographic" },
  { id: "painterly", label: "Painterly" },
];
import { toast } from "sonner";

const EMPTY_RESEARCH = { title: "", source: "", url: "", summary: "", tradition: "" };
const EMPTY_PRODUCT = { name: "", category: "", summary: "", url: "", image: "" };
const EMPTY_LIBRARY = { title: "", category: "", tradition: "", reading_time: "", excerpt: "", image: "", body: "", status: "draft", tags: [], product_ids: [] };
const EMPTY_STEWARD = {
  name: "", role: "Vitality Steward", credentials: "", bio: "", image: "",
  supervising_clinician: "", specialties: "", location: "", status: "published",
};
const EMPTY_HEALER = {
  name: "", role: "Traditional Healer", tradition: "", bio: "", image: "",
  location: "", specialties: "", contact: "", status: "published",
};

const TABS = ["research", "products", "library", "stewards", "applications", "healers", "healerApplications"];
const TAB_LABEL = {
  research: "Research", products: "Products", library: "Library", stewards: "Stewards", applications: "Steward Apps",
  healers: "Healers", healerApplications: "Healer Apps",
};

const ApplicationList = ({ items, onApprove, onReject, onDelete }) => (
  <>
    <p className="label-eyebrow mb-4">{items.length} application{items.length === 1 ? "" : "s"}</p>
    <div className="space-y-3">
      {items.map((a) => (
        <div key={a.id} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-5" data-testid={`application-item-${a.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${a.status === "approved" ? "bg-[#E7EEE3] text-[#3F6B45]" : a.status === "rejected" ? "bg-[#F1E0DD] text-[#8F3A3A]" : "bg-[#EDE7DC] text-[#8a6620]"}`} data-testid={`application-status-${a.id}`}>
                {a.status}
              </span>
              <p className="font-serif text-lg text-[#2D322B] mt-2">{a.name}</p>
              <p className="text-xs text-[#5C6359]">{a.email}{a.phone ? ` · ${a.phone}` : ""}{a.location ? ` · ${a.location}` : ""}</p>
              {a.tradition && <p className="text-xs text-[#7A4A6B] mt-1">Tradition: {a.tradition}</p>}
              {a.specialties && <p className="text-xs text-[#C05C42] mt-1">{a.specialties}</p>}
              {a.supervising_clinician && <p className="text-xs text-[#5C6359] mt-1">Prefers: {a.supervising_clinician}</p>}
              {a.contact && <p className="text-xs text-[#5C6359] mt-1">Contact: {a.contact}</p>}
              <p className="text-sm text-[#5C6359] mt-2"><span className="text-[10px] uppercase tracking-widest text-[#9A8F7E]">Background</span><br />{a.background}</p>
              <p className="text-sm text-[#5C6359] mt-2"><span className="text-[10px] uppercase tracking-widest text-[#9A8F7E]">Motivation</span><br />{a.motivation}</p>
            </div>
            {a.status === "pending" && (
              <div className="shrink-0 flex flex-col gap-2">
                <button onClick={() => onApprove(a.id)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#3F6B45] hover:text-[#2D322B] border border-[#BBD0B7] rounded-full px-3 py-1.5" data-testid={`approve-${a.id}`}>
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button onClick={() => onReject(a.id)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#8F3A3A] hover:text-[#C05C42] border border-[#E0C7BC] rounded-full px-3 py-1.5" data-testid={`reject-${a.id}`}>
                  <Ban className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            )}
            {a.status !== "pending" && (
              <button onClick={() => onDelete(a.id)} className="shrink-0 text-[#8F3A3A] hover:text-[#C05C42]" data-testid={`delete-application-${a.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ))}
      {items.length === 0 && (
        <p className="text-sm text-[#5C6359] bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6">No applications yet.</p>
      )}
    </div>
  </>
);

export default function AdminCurate() {
  const { user } = useAuth();
  const [tab, setTab] = useState("research");
  const [research, setResearch] = useState([]);
  const [products, setProducts] = useState([]);
  const [library, setLibrary] = useState([]);
  const [libQuery, setLibQuery] = useState("");
  const [libCat, setLibCat] = useState("all");
  const [stewards, setStewards] = useState([]);
  const [applications, setApplications] = useState([]);
  const [healers, setHealers] = useState([]);
  const [healerApps, setHealerApps] = useState([]);
  const [apoProducts, setApoProducts] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState(null); // { theme, topic, kind } when the AI draft dialog is open
  const [aiBusy, setAiBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  const reload = async () => {
    const [r, p, lib, s, a, h, ha, apo] = await Promise.all([
      api.get("/curated/research"),
      api.get("/curated/products"),
      api.get("/admin/library"),
      api.get("/admin/stewards"),
      api.get("/admin/steward-applications"),
      api.get("/admin/healers"),
      api.get("/admin/healer-applications"),
      api.get("/admin/apothecary"),
    ]);
    setResearch(r.data);
    setProducts(p.data);
    setLibrary(lib.data);
    setStewards(s.data);
    setApplications(a.data);
    setHealers(h.data);
    setHealerApps(ha.data);
    setApoProducts(apo.data || []);
  };

  useEffect(() => { if (user?.is_admin) reload(); }, [user]);

  const ENDPOINT = { research: "/curated/research", product: "/curated/products", article: "/admin/library", steward: "/admin/stewards", healer: "/admin/healers" };
  const draftKindFor = (t) => ({ research: "research", products: "product", library: "article", stewards: "steward", healers: "healer" }[t] || "research");
  const emptyFor = (kind) => ({ research: EMPTY_RESEARCH, product: EMPTY_PRODUCT, article: EMPTY_LIBRARY, steward: EMPTY_STEWARD, healer: EMPTY_HEALER }[kind]);

  const generateImage = async (style = "botanical") => {
    if (!draft?.title && !draft?.category) { toast.error("Add a title or category first"); return; }
    setImgBusy(style);
    try {
      const r = await api.post("/admin/library/generate-image", { title: draft.title, category: draft.category, style });
      setDraft((d) => ({ ...d, image: r.data.url }));
      toast.success("Massey painted a hero image.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate image");
    } finally {
      setImgBusy(null);
    }
  };

  const startNew = (kind) => setDraft({ kind, ...emptyFor(kind) });
  const startEdit = (kind, item) => setDraft({ kind, ...(kind === "article" ? { status: "published" } : {}), ...item });

  const generateDraft = async () => {
    if (!ai?.theme) { toast.error("Pick a theme first"); return; }
    setAiBusy(true);
    try {
      if (ai.kind === "article") {
        const r = await api.post("/admin/library/draft", { theme: ai.theme, topic: ai.topic || null });
        setAi(null);
        setDraft({ kind: "article", ...EMPTY_LIBRARY, ...r.data });
        toast.success("Massey drafted a full article — refine it, add an image, then Save to publish.");
      } else {
        const r = await api.post("/admin/curated/draft", { theme: ai.theme, topic: ai.topic || null });
        setAi(null);
        setDraft({ kind: "research", ...EMPTY_RESEARCH, ...r.data });
        toast.success("Massey drafted a card — review, add a source link, then save.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft");
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { kind, id, ...body } = draft;
      delete body._id; delete body.created_at; delete body.kind;
      const endpoint = ENDPOINT[kind];
      if (id) {
        await api.put(`${endpoint}/${id}`, body);
        toast.success("Saved");
      } else {
        await api.post(endpoint, body);
        toast.success("Added");
      }
      setDraft(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`${ENDPOINT[kind]}/${id}`);
      toast.success("Removed");
      reload();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const toggleStatus = async (it) => {
    const next = it.status === "draft" ? "published" : "draft";
    try {
      const { _id, created_at, ...body } = it;
      await api.put(`${ENDPOINT.article}/${it.id}`, { ...body, status: next });
      setLibrary((arr) => arr.map((a) => (a.id === it.id ? { ...a, status: next } : a)));
      toast.success(next === "published" ? "Published — now visible to members." : "Moved to draft — hidden from members.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };

  const reviewApplication = async (aid, status, base) => {
    try {
      await api.patch(`${base}/${aid}`, { status });
      toast.success(status === "approved" ? "Approved & published to directory" : "Application rejected");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const deleteApplication = async (aid, base) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await api.delete(`${base}/${aid}`);
      toast.success("Application removed");
      reload();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const FIELDS = {
    research: [
      { name: "title", label: "Title" },
      { name: "source", label: "Source (e.g. Cell Metabolism · 2026)" },
      { name: "url", label: "URL" },
      { name: "summary", label: "Summary", multiline: true },
      { name: "tradition", label: "Tradition tag (e.g. Precision Medicine)" },
    ],
    product: [
      { name: "name", label: "Product name" },
      { name: "category", label: "Category (e.g. Adaptogen)" },
      { name: "summary", label: "Summary", multiline: true },
      { name: "url", label: "URL" },
      { name: "image", label: "Image URL" },
    ],
    article: [
      { name: "title", label: "Title" },
      { name: "category", label: "Category (e.g. Nutritional Medicine, Herbal Medicine)" },
      { name: "tradition", label: "Tradition (e.g. First Nations Australia, Gangulu × Modern science, Integrative)" },
      { name: "reading_time", label: "Reading time (e.g. 8 min)" },
      { name: "image", label: "Image URL" },
      { name: "excerpt", label: "Excerpt (short teaser shown on the card)", multiline: true },
      { name: "body", label: "Body (full article)", multiline: true, big: true },
    ],
    steward: [
      { name: "name", label: "Full name" },
      { name: "role", label: "Role (default: Vitality Steward)" },
      { name: "credentials", label: "Credentials / focus (optional)" },
      { name: "bio", label: "Bio", multiline: true },
      { name: "supervising_clinician", label: "Supervising clinician (e.g. Dr Sam Toby)" },
      { name: "specialties", label: "Specialties (optional)" },
      { name: "location", label: "Location (optional)" },
      { name: "image", label: "Image URL (optional)" },
    ],
    healer: [
      { name: "name", label: "Full name" },
      { name: "role", label: "Role (default: Traditional Healer)" },
      { name: "tradition", label: "Tradition / lineage (e.g. Gangulu, Aboriginal & Torres Strait Islander)" },
      { name: "bio", label: "Bio", multiline: true },
      { name: "location", label: "Region (optional)" },
      { name: "specialties", label: "Specialties (optional)" },
      { name: "contact", label: "Contact for members (optional)" },
      { name: "image", label: "Image URL (optional)" },
    ],
  };

  return (
    <AdminLayout>
      <div data-testid="admin-curate-page">
        <p className="label-eyebrow mb-2">Admin · Curation</p>
        <h1 className="font-serif text-4xl text-[#2D322B] leading-tight">Curate the experience.</h1>
        <p className="mt-4 text-[#5C6359] max-w-2xl text-sm">
          Research &amp; products appear on /digest. Stewards appear in /practitioners. Edits are live the moment you save — no deploy.
        </p>

        <div className="mt-8 inline-flex flex-wrap p-1 rounded-full bg-[#F1ECE4] border border-[#D1C7B8]" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setDraft(null); }}
              data-testid={`tab-${t}`}
              className={`px-6 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${tab === t ? "bg-[#2D322B] text-[#F9F6F0]" : "text-[#2D322B] hover:text-[#C05C42]"}`}
            >
              {TAB_LABEL[t]}
              {t === "applications" && applications.filter((a) => a.status === "pending").length > 0 && (
                <span className="ml-2 inline-block bg-[#C05C42] text-[#F9F6F0] rounded-full px-2 text-[10px]" data-testid="applications-pending-count">
                  {applications.filter((a) => a.status === "pending").length}
                </span>
              )}
            </button>
          ))}
        </div>

      <section className="mt-8">
        {tab === "applications" ? (
          <ApplicationList
            items={applications}
            onApprove={(id) => reviewApplication(id, "approved", "/admin/steward-applications")}
            onReject={(id) => reviewApplication(id, "rejected", "/admin/steward-applications")}
            onDelete={(id) => deleteApplication(id, "/admin/steward-applications")}
          />
        ) : tab === "healerApplications" ? (
          <ApplicationList
            items={healerApps}
            onApprove={(id) => reviewApplication(id, "approved", "/admin/healer-applications")}
            onReject={(id) => reviewApplication(id, "rejected", "/admin/healer-applications")}
            onDelete={(id) => deleteApplication(id, "/admin/healer-applications")}
          />
        ) : (
          <>
            {(() => {
              const rawItems = tab === "research" ? research : tab === "products" ? products : tab === "library" ? library : tab === "stewards" ? stewards : healers;
              const kind = draftKindFor(tab);
              let items = rawItems;
              let libCats = [];
              if (tab === "library") {
                libCats = Array.from(new Set(library.map((a) => a.category).filter(Boolean))).sort();
                const q = libQuery.trim().toLowerCase();
                items = rawItems.filter((a) => {
                  if (libCat !== "all" && a.category !== libCat) return false;
                  if (!q) return true;
                  const hay = [a.title, a.excerpt, a.body, a.tradition, (a.tags || []).join(" ")].join(" ").toLowerCase();
                  return hay.includes(q);
                });
              }
              return (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <p className="label-eyebrow">{tab === "library" ? `${items.length} of ${rawItems.length}` : items.length} {tab} on file</p>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                      {(tab === "research" || tab === "library") && (
                        <button onClick={() => setAi({ theme: DRAFT_THEMES[0], topic: "", kind: draftKindFor(tab) })} className="btn-secondary !py-2 !px-5 inline-flex items-center justify-center gap-2" data-testid="ai-draft-open">
                          <Sparkles className="w-4 h-4" /> Draft with Massey
                        </button>
                      )}
                      <button onClick={() => startNew(kind)} className="btn-primary !py-2 !px-5 inline-flex items-center justify-center gap-2" data-testid={`add-${tab}`}>
                        <Plus className="w-4 h-4" /> Add {kind}
                      </button>
                    </div>
                  </div>
                  {tab === "library" && (
                    <div className="flex flex-col sm:flex-row gap-3 mb-5">
                      <div className="relative flex-1">
                        <Search className="w-4 h-4 text-[#9A8F7E] absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          value={libQuery}
                          onChange={(e) => setLibQuery(e.target.value)}
                          placeholder="Search title, excerpt, body or tags…"
                          data-testid="library-search"
                          className="w-full bg-[#F1ECE4] border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl pl-9 pr-3 py-2.5 outline-none text-[#2D322B]"
                        />
                      </div>
                      <select
                        value={libCat}
                        onChange={(e) => setLibCat(e.target.value)}
                        data-testid="library-category-filter"
                        className="bg-[#F1ECE4] border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl px-3 py-2.5 outline-none text-[#2D322B] sm:w-56"
                      >
                        <option value="all">All categories ({library.length})</option>
                        {libCats.map((c) => (
                          <option key={c} value={c}>{c} ({library.filter((a) => a.category === c).length})</option>
                        ))}
                      </select>
                      {(libQuery || libCat !== "all") && (
                        <button onClick={() => { setLibQuery(""); setLibCat("all"); }} data-testid="library-clear-filters" className="btn-secondary !py-2 !px-4 text-xs whitespace-nowrap">Clear</button>
                      )}
                    </div>
                  )}
                  <div className="space-y-3">
                    {tab === "library" && items.length === 0 && (
                      <p className="text-sm text-[#5C6359] bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6" data-testid="library-no-results">No writings match your search.</p>
                    )}
                    {items.map((it) => (
                      <div key={it.id} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-5 flex items-start gap-4" data-testid={`${tab}-item-${it.id}`}>
                        <div className="flex-1 min-w-0">
                          {tab === "research" && (
                            <>
                              <p className="text-xs uppercase tracking-widest text-[#C05C42]">{it.tradition || "—"}</p>
                              <p className="font-serif text-lg text-[#2D322B] mt-1">{it.title}</p>
                              <p className="text-xs text-[#5C6359] italic">{it.source}</p>
                              <p className="text-sm text-[#5C6359] mt-1">{it.summary}</p>
                            </>
                          )}
                          {tab === "products" && (
                            <>
                              <p className="text-xs uppercase tracking-widest text-[#C05C42]">{it.category}</p>
                              <p className="font-serif text-lg text-[#2D322B] mt-1">{it.name}</p>
                              <p className="text-sm text-[#5C6359] mt-1">{it.summary}</p>
                            </>
                          )}
                          {tab === "library" && (
                            <>
                              <div className="flex items-center gap-2">
                                <p className="text-xs uppercase tracking-widest text-[#C05C42]">{it.category}{it.reading_time ? ` · ${it.reading_time}` : ""}</p>
                                <span data-testid={`library-status-${it.id}`} className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${(it.status === "draft") ? "bg-[#D49A44]/20 text-[#8a6320]" : "bg-[#3F6B4C]/15 text-[#3F6B4C]"}`}>
                                  {it.status === "draft" ? "Draft" : "Published"}
                                </span>
                              </div>
                              <p className="font-serif text-lg text-[#2D322B] mt-1">{it.title}</p>
                              {it.tradition && <p className="text-xs text-[#5C6359]">Tradition · {it.tradition}</p>}
                              <p className="text-sm text-[#5C6359] mt-1 line-clamp-2">{it.excerpt || it.body}</p>
                            </>
                          )}
                          {tab === "stewards" && (
                            <>
                              <p className="text-xs uppercase tracking-widest text-[#3F6B45]">{it.role}{it.location ? ` · ${it.location}` : ""}</p>
                              <p className="font-serif text-lg text-[#2D322B] mt-1">{it.name}</p>
                              {it.supervising_clinician && <p className="text-xs text-[#5C6359]">Supervised by {it.supervising_clinician}</p>}
                              <p className="text-sm text-[#5C6359] mt-1">{it.bio}</p>
                            </>
                          )}
                          {tab === "healers" && (
                            <>
                              <p className="text-xs uppercase tracking-widest text-[#7A4A6B]">{it.role}{it.location ? ` · ${it.location}` : ""}</p>
                              <p className="font-serif text-lg text-[#2D322B] mt-1">{it.name}</p>
                              {it.tradition && <p className="text-xs text-[#7A4A6B]">{it.tradition}</p>}
                              <p className="text-sm text-[#5C6359] mt-1">{it.bio}</p>
                            </>
                          )}
                        </div>
                        <div className="shrink-0 flex flex-col gap-2">
                          {tab === "library" && (
                            <button onClick={() => toggleStatus(it)} data-testid={`toggle-status-${it.id}`}
                              className="text-xs uppercase tracking-widest text-[#3F6B4C] hover:text-[#2D322B]">
                              {it.status === "draft" ? "Publish" : "Unpublish"}
                            </button>
                          )}
                          <button onClick={() => startEdit(kind, it)} className="text-xs uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B]" data-testid={`edit-${it.id}`}>Edit</button>
                          <button onClick={() => remove(kind, it.id)} className="text-[#8F3A3A] hover:text-[#C05C42]" data-testid={`delete-${it.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {items.length === 0 && (
                      <p className="text-sm text-[#5C6359] bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6">Nothing here yet.</p>
                    )}
                  </div>
                </>
              );
            })()}
          </>
        )}
      </section>

      {/* AI draft (Massey) dialog */}
      {ai && (
        <div className="fixed inset-0 z-50 bg-[#2D322B]/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ai-draft-modal">
          <div className="bg-[#F9F6F0] rounded-3xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-[#D1C7B8] shrink-0">
              <div>
                <p className="label-eyebrow text-[#D49A44] inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Draft with Massey</p>
                <h2 className="font-serif text-2xl text-[#2D322B] mt-1">{ai.kind === "article" ? "Generate a full article" : "Generate a research card"}</h2>
              </div>
              <button onClick={() => setAi(null)} className="text-[#5C6359] hover:text-[#C05C42]" data-testid="ai-draft-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Theme</label>
                <select
                  value={ai.theme}
                  onChange={(e) => setAi({ ...ai, theme: e.target.value })}
                  data-testid="ai-draft-theme"
                  className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B]"
                >
                  {DRAFT_THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Topic / angle (optional)</label>
                <textarea
                  rows={3}
                  value={ai.topic}
                  onChange={(e) => setAi({ ...ai, topic: e.target.value })}
                  placeholder="e.g. Kakadu plum and polyphenols for metabolic health"
                  data-testid="ai-draft-topic"
                  className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B]"
                />
              </div>
              <p className="text-xs text-[#5C6359]">{ai.kind === "article"
                ? "Massey writes a full long-form article (title, category, excerpt & body). Refine it, add an image, then Save to publish it live to the Library."
                : "Massey drafts a headline, source style, summary and tag. Review it, paste a real source link, then save — nothing publishes automatically."}</p>
            </div>
            <div className="p-6 border-t border-[#D1C7B8] flex justify-end gap-3 shrink-0">
              <button onClick={() => setAi(null)} className="btn-secondary !py-2 !px-5">Cancel</button>
              <button onClick={generateDraft} disabled={aiBusy} className="btn-primary !py-2 !px-5 inline-flex items-center gap-2" data-testid="ai-draft-generate">
                <Sparkles className="w-4 h-4" /> {aiBusy ? "Drafting…" : "Draft it"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draft editor modal */}
      {draft && (
        <div className="fixed inset-0 z-50 bg-[#2D322B]/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="draft-modal">
          <div className="bg-[#F9F6F0] rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-[#D1C7B8] shrink-0">
              <div>
                <p className="label-eyebrow text-[#D49A44]">{draft.id ? "Edit" : "New"}</p>
                <h2 className="font-serif text-2xl text-[#2D322B] mt-1 capitalize">{draft.kind}</h2>
              </div>
              <button onClick={() => setDraft(null)} className="text-[#5C6359] hover:text-[#C05C42]" data-testid="draft-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {FIELDS[draft.kind].map((f) => (
                <div key={f.name}>
                  <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">{f.label}</label>
                  {f.multiline ? (
                    <textarea
                      rows={f.big ? 14 : 3}
                      value={draft[f.name] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value })}
                      data-testid={`field-${f.name}`}
                      className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B]"
                    />
                  ) : (
                    <input
                      value={draft[f.name] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value })}
                      data-testid={`field-${f.name}`}
                      className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
                    />
                  )}
                  {f.name === "image" && draft.kind === "article" && (
                    <div className="mt-2 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest text-[#5C6359] mr-1">{draft.image ? "Regenerate:" : "Generate:"}</span>
                        {IMG_STYLES.map((st) => (
                          <button key={st.id} type="button" onClick={() => generateImage(st.id)} disabled={!!imgBusy}
                            data-testid={`generate-image-${st.id}`}
                            className="btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" /> {imgBusy === st.id ? "Painting…" : st.label}
                          </button>
                        ))}
                      </div>
                      {draft.image && (
                        <img src={draft.image} alt="hero preview" data-testid="image-preview"
                          className="w-full h-40 object-cover rounded-xl border border-[#D1C7B8]" />
                      )}
                    </div>
                  )}
                </div>
              ))}
              {draft.kind === "article" && (
                <div className="space-y-4 pt-2 border-t border-[#D1C7B8]">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-2">Status</label>
                    <div className="inline-flex rounded-xl border border-[#D1C7B8] overflow-hidden" data-testid="article-status-toggle">
                      {["draft", "published"].map((st) => (
                        <button key={st} type="button" onClick={() => setDraft({ ...draft, status: st })} data-testid={`article-status-${st}`}
                          className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${(draft.status || "draft") === st ? (st === "published" ? "bg-[#3F6B4C] text-white" : "bg-[#D49A44] text-[#2D322B]") : "bg-transparent text-[#5C6359] hover:bg-[#F1ECE4]"}`}>
                          {st}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#9A8F7E] mt-1">Drafts are hidden from members everywhere; publish when it's ready to go live.</p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Tags (comma-separated — used to auto-match products &amp; related content)</label>
                    <input
                      value={(draft.tags || []).join(", ")}
                      onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                      placeholder="e.g. respiratory, anti-inflammatory, herbal"
                      data-testid="field-tags"
                      className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-2">
                      Linked products (hand-pick which Apothecary products show under this writing)
                    </label>
                    <p className="text-[11px] text-[#9A8F7E] mb-2">
                      Leave all unchecked to auto-match by category, then tags. Tick any to override with an exact selection.
                    </p>
                    <div className="max-h-52 overflow-y-auto rounded-xl border border-[#D1C7B8] divide-y divide-[#E4DACb]" data-testid="linked-products-picker">
                      {apoProducts.length === 0 ? (
                        <p className="text-sm text-[#5C6359] p-3">No Apothecary products yet.</p>
                      ) : apoProducts.map((p) => {
                        const selected = (draft.product_ids || []).includes(p.id);
                        return (
                          <label key={p.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-[#F1ECE4]" data-testid={`linked-product-${p.id}`}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const cur = draft.product_ids || [];
                                setDraft({ ...draft, product_ids: e.target.checked ? [...cur, p.id] : cur.filter((x) => x !== p.id) });
                              }}
                              data-testid={`linked-product-check-${p.id}`}
                              className="accent-[#C05C42] w-4 h-4"
                            />
                            <div className="w-8 h-8 rounded-md overflow-hidden bg-[#EAEFE4] shrink-0">
                              {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-[#2D322B] truncate">{p.name}</p>
                              <p className="text-[11px] text-[#9A8F7E] truncate">{p.category}{p.price_aud != null ? ` · $${p.price_aud} AUD` : ""}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {(draft.product_ids || []).length > 0 && (
                      <p className="text-[11px] text-[#3F6B4C] mt-2" data-testid="linked-products-count">{(draft.product_ids || []).length} product(s) hand-picked.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-[#D1C7B8] flex justify-end gap-3 shrink-0">
              <button onClick={() => setDraft(null)} className="btn-secondary !py-2 !px-5">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5 inline-flex items-center gap-2" data-testid="draft-save">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
